import { useDispatch, useSelector } from "react-redux";
import Table from "@/components/Table/table";
import TitleComplement from "@/features/creditor/claimsTab/components/titleComplement";
import { caseRenderers } from "@/features/creditor/claimsTab/Constant/creditorCustomCellConst";
import { useColumnDefs } from "@/features/creditor/claimsTab/Constant/creditorHeaderConst";
import {
  useGetCreditorClaimListApiDataQuery,
  useLazyGetClaimBasicSearchCreditorTabQuery,
} from "@/services/cases/casesReducer";
import { MotifProgressLoader } from "@ey-xd/motif-react";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCase } from "@/features/case/use-case";
import { useTranslation } from "react-i18next";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import CustomPagination from "@/components/Pagination/CustomPagination";
import BasicSearchClaimsTab from "./BasicSearchClaimsTab";
import { cleanPayload } from "@/utility/common";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";

export default function ClaimTabContent({
  creditorDetails,
}: {
  creditorDetails?: any;
}) {
  const { caseId, creditorId } = useParams();
  const { t } = useTranslation();
  let tableLoadingData = customTableLoadingData;
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  const retainState = useSelector(
    (e: any) => e.retainState.creditorClaimsTabState
  );
  const [isBasicSearchActive, setIsBasicSearchActive] = useState(false);
  const [
    triggerBasicSearch,
    {
      data: searchBasicData,
      isFetching: basicSearchFetching,
      error: basicSearchError,
      status: basicSearchStatus,
    },
  ] = useLazyGetClaimBasicSearchCreditorTabQuery();
  const [searchQuery, setSearchQuery] = useState(retainState.SearchText || "");

  const caseData = useCase("", true);
  const abbreviation = caseData?.abbreviation;
  const columnDefs = useColumnDefs({ caseId, creditorId, abbreviation });

  const dispatch = useDispatch();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseID: caseId || caseData.caseId,
      CreditorId: creditorId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  },[retainState, caseId, creditorId, caseData.caseId]);
  const paginationObj = getPaginationObj;
  const isBasicSearchApplied = retainState.isBasicSearchApplied || false;

  const { data, error, isLoading, refetch, isFetching,status } =
    useGetCreditorClaimListApiDataQuery(
      {
        ...paginationObj,
      },
      { skip: isBasicSearchApplied }
    );
  const basicSearchPayload = retainState.basicSearchPayload;

  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "creditorClaimsTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }
  let resetSortStatus = useCallback(
    (defaultSort: string) => {
      let json: any = { ...retainState.sortedColumnTypes };
      for (const key in json) {
        json[key] = { sort: null };
      }
      json[defaultSort] = { sort: "desc" };
      return json;
    },
    [retainState]
  );

  const showBasicSearch = useSelector(
    (state: any) => state.claims.showBasicSearch
  );

  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchQuery(searchValue);

      if (searchValue.trim() === "") {
        setIsBasicSearchActive(false);
        dispatch(
          setComponentState1({
            entityName: "creditorClaimsTabState",
            entityState: {
              sortedColumnTypes: { ...resetSortStatus("updatedOnType") },
              PageNumber: 1,
              SearchText: "",
              SortOrder: "desc",
              SortColumn: "UpdatedOn",
              PageSize: paginationPageSize,
              basicSearchPayload: {},
              isBasicSearchApplied: false,
            },
          })
        );
      } else {
        setIsBasicSearchActive(false);
        dispatch(
          setComponentState1({
            entityName: "creditorClaimsTabState",
            entityState: {
              sortedColumnTypes: { ...resetSortStatus("updatedOnType") },
              PageNumber: 1,
              SearchText: searchValue,
              SortOrder: "desc",
              SortColumn: "UpdatedOn",
              PageSize: paginationPageSize,
              basicSearchPayload: {},
              isBasicSearchApplied: false,
            },
          })
        );
      }
    },
    [
      resetSortStatus,
      paginationPageSize,
      dispatch,
    ]
  );

  const handleQuickSearchClear = () => {
    setSearchQuery("");
    setIsBasicSearchActive(false);
    setSearchQuery("");
    dispatch(
      setComponentState1({
        entityName: "creditorClaimsTabState",
        entityState: {
          sortedColumnTypes: { ...resetSortStatus("updatedOnType") },
          PageNumber: 1,
          SearchText: "",
          SortOrder: "desc",
          SortColumn: "UpdatedOn",
          PageSize: paginationPageSize,
          basicSearchPayload: {},
          isBasicSearchApplied: false,
        },
      })
    );
  };

  const headerData = {
    title: "pages.creditors.creditorClaimTab.tableTitle",
    description: "pages.creditors.creditorClaimTab.tableDescription",
  };
  useEffect(() => {
    if (isBasicSearchApplied) {
      const cleanedBasicSearchPayload = cleanPayload(basicSearchPayload);
      triggerBasicSearch({ ...cleanedBasicSearchPayload, ...paginationObj });
    } else {
      refetch();
    }
  }, [triggerBasicSearch, refetch,isBasicSearchApplied,basicSearchPayload,paginationObj]);

  const handleBasicSearch = (searchParams: any) => {
    setSearchQuery("");
    setIsBasicSearchActive(false);
    dispatch(
      setComponentState1({
        entityName: "creditorClaimsTabState",
        entityState: {
          sortedColumnTypes: { ...resetSortStatus("updatedOnType") },
          PageNumber: 1,
          SearchText: "",
          SortOrder: "desc",
          SortColumn: "UpdatedOn",
          PageSize: paginationPageSize,
          basicSearchPayload: searchParams,
          isBasicSearchApplied: true,
        },
      })
    );
  };
  if (isLoading) {
    return <MotifProgressLoader show variant="default" />;
  }

  const tableData = isBasicSearchApplied
    ? searchBasicData?.results
    : data?.results
    ? data.results
    : [];
  const datafetching = isFetching || basicSearchFetching;
  const dataError = isBasicSearchApplied ? basicSearchError : error;
  const hasMoreRecords = isBasicSearchApplied
    ? searchBasicData?.hasMoreRecords
    : data?.hasMoreRecords;
    const apiRes = isBasicSearchApplied
    ? searchBasicData
    : data
    const apiStatus = isBasicSearchApplied
    ? basicSearchStatus
    : status


  return (
    <>
      <div
        className={
          "ccwp_table_tabpanel " + (showBasicSearch ? "hide_claims_table" : "")
        }
      >
        <>
          <div
            className={
              isBasicSearchActive
                ? "hide_claims_table crediotor_claimsTab"
                : "crediotor_claimsTab"
            }
          >
            <Table
              columnDefs={columnDefs}
              columnTypes={{ ...retainState.sortedColumnTypes }}
              handlePaginationData={handlePaginationData}
              paginationObj={paginationObj}
              cellRenderers={datafetching ? [] : tableData}
              TitleComplement={() => (
                <TitleComplement
                  onSearch={handleSearch}
                  setIsBasicSearchActive={setIsBasicSearchActive}
                  isBasicSearchApplied={isBasicSearchApplied}
                  isBasicSearchActive={isBasicSearchActive}
                  caseId={caseId || ""}
                  handleQuickSearchClear={handleQuickSearchClear}
                  searchQuery={searchQuery}
                  isBasicSearch={false}
                  handleClearBasicSearch={() => {}}
                  creditorDetails={creditorDetails}
                />
              )}
              title={headerData.title}
              description={headerData.description}
              caseRenderers={caseRenderers}
              overlayNoRowsTemplate={
                datafetching
                  ? t(tableLoadingData)
                  : dataError
                  ? t("global.globalNotifications.wentWrong")
                  : t("global.globalNotifications.noRecords")
              }
            />
            {!isBasicSearchActive && (
              <CustomPagination
              apiData={apiRes}
              apiStatus={apiStatus}
                currentpage={paginationObj.PageNumber}
                pageSize={paginationObj.PageSize}
                disableNext={!hasMoreRecords}
                onChangePageSize={(page: number) => {
                  dispatch(
                    setComponentState1({
                      entityName: "creditorClaimsTabState",
                      entityState: {
                        PageSize: page,
                        PageNumber: 1,
                      },
                    })
                  );
                }}
                onChangeNextPage={() => {
                  dispatch(
                    setComponentState1({
                      entityName: "creditorClaimsTabState",
                      entityState: {
                        PageNumber: paginationObj.PageNumber + 1,
                      },
                    })
                  );
                }}
                onChangePreviousPage={() => {
                  dispatch(
                    setComponentState1({
                      entityName: "creditorClaimsTabState",
                      entityState: {
                        PageNumber: paginationObj.PageNumber - 1,
                      },
                    })
                  );
                }}
                pageSizeSelector={paginationPageSizeSelector}
              />
            )}
          </div>
        </>
      </div>
      {isBasicSearchActive ? (
        <BasicSearchClaimsTab
          name={creditorDetails?.creditorName || ""}
          selectedId={creditorDetails?.creditorReference || ""}
          onClose={() => setIsBasicSearchActive(false)}
          onApply={handleBasicSearch}
          basicSearchPayload={basicSearchPayload}
        />
      ) : (
        <></>
      )}
    </>
  );
}
